import { DeviceDetails, ErrorStatus, Matter } from '@clipsalsolar-se/capacitor-matter';
import * as Sentry from '@sentry/react';

import { baseApi } from '../../../../app/services/baseApi';
import { RTKQError } from '../../../../common/api/api-helpers';
import { IS_NATIVE } from '../../../../common/constants';
import { MATTER_ON_OFF_ENDPOINT_ID, MATTER_OPEN_COMMISSIONING_WINDOW_DURATION_SECONDS } from './matter-helpers';

export type LocalMatterDevice = Partial<DeviceDetails> & { matterDeviceId: string };

type AddLocalMatterDeviceIOS = { homeName: string };
type AddLocalMatterDeviceAndroid = {
  manualCode: string | null;
  qrCode: string | null;
  ssid: string;
  ssidPassword: string;
};

const MAX_TIMEOUT_MS = 2000;

/*
 * This API is used to manage local Matter devices (native only) via our custom capacitor-matter plugin.
 * NOTE: The Matter spec is large and complex, so it is recommended to refer to Matter documentation for a better --
 * -- understanding of the protocol. However for simplicity, we use 'siteId's as 'fabricId's in the Matter spec.
 */
export const localAylaMatterDevicesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLocalAylaMatterDevices: builder.query<LocalMatterDevice[], void>({
      queryFn: async () => {
        try {
          const { devices } = await Matter.getDevices(); // This returns Matter plugin deviceIds -
          // -- (auto-generated from the plugin, not tied to our API switch IDs in anyway).
          // So we can map these to our API switch IDs, we need to retrieve device details for each device locally.

          // If a locally commissioned Matter device has been factory reset, or it is not accessible via the current --
          // -- Wi-Fi network conditions, getDeviceDetails() will time out (>30s) and doesn't tell us why. So here --
          // -- we gracefully handle this in case of that. @TODO: Fix this issue in the capacitor-matter plugin
          const fetchDeviceDetailsWithTimeout = async (matterDeviceId: string) => {
            return new Promise<LocalMatterDevice>((resolve, reject) => {
              const timeoutId = setTimeout(() => {
                reject(new Error('Timeout'));
              }, MAX_TIMEOUT_MS);

              Matter.getDeviceDetails({ deviceId: matterDeviceId })
                .then((deviceDetails) => {
                  clearTimeout(timeoutId); // Clear the timeout if fetch is successful
                  resolve({ matterDeviceId, ...deviceDetails });
                })
                .catch((error) => {
                  clearTimeout(timeoutId); // Clear the timeout in case of error
                  reject(error);
                });
            });
          };

          const localMatterDevices: LocalMatterDevice[] = [];
          for (const deviceId of devices) {
            try {
              const deviceDetails = await fetchDeviceDetailsWithTimeout(deviceId);
              localMatterDevices.push(deviceDetails);
            } catch (error) {
              console.error(error);
              localMatterDevices.push({ matterDeviceId: deviceId });
            }
          }

          return { data: localMatterDevices };
        } catch (e) {
          Sentry.captureException(e, {
            tags: {
              category: 'feature',
              feature: 'ayla-switches',
              helper: 'localAylaMatterDevicesApi',
              method: 'getLocalAylaMatterDevices',
            },
          });
          const error = e as { message: ErrorStatus };
          console.error(error);
          const message = error?.message ?? 'Error getting local Matter devices';
          return { error: { status: 500, message } as RTKQError };
        }
      },
      providesTags: ['LocalMatterDevices'],
    }),

    commissionLocalAylaMatterDeviceIOS: builder.mutation<
      { didComplete: boolean; deviceId?: string },
      AddLocalMatterDeviceIOS
    >({
      queryFn: async ({ homeName }) => {
        try {
          const commissioningResult = await Matter.startNativeCommissioningWizard({ homeName });
          return { data: commissioningResult };
        } catch (e) {
          Sentry.captureException(e, {
            tags: {
              category: 'feature',
              feature: 'ayla-switches',
              helper: 'localAylaMatterDevicesApi',
              method: 'commissionLocalAylaMatterDeviceIOS',
            },
          });
          const error = e as { message: ErrorStatus };
          console.error(error);
          const message = error?.message ?? 'Error commissioning local Matter device on iOS';
          return { error: { status: 500, message } as RTKQError };
        }
      },
      invalidatesTags: ['LocalMatterDevices'],
    }),
    commissionLocalAylaMatterDeviceAndroid: builder.mutation<{ deviceId: string }, AddLocalMatterDeviceAndroid>({
      queryFn: async ({ manualCode, qrCode, ssid, ssidPassword }) => {
        try {
          if (manualCode) {
            const commissioningResult = await Matter.manualCodeCommissioning({
              manualCode: manualCode.trim(),
              ssid,
              ssidPassword,
            });
            return { data: commissioningResult };
          }
          if (qrCode) {
            const commissioningResult = await Matter.qrCodeCommissioning({
              qrCodeId: qrCode.trim(),
              ssid,
              ssidPassword,
            });
            return { data: commissioningResult };
          }
          throw new Error('No commissioning code provided');
        } catch (e) {
          const error = e as { message: ErrorStatus };
          console.error(error);
          const message = error?.message ?? 'Error commissioning local Matter device on Android';
          return { error: { status: 500, message } as RTKQError };
        }
      },
      invalidatesTags: ['LocalMatterDevices'],
    }),
    deleteLocalAylaMatterDevice: builder.mutation<void, { matterDeviceId: string }>({
      queryFn: /* istanbul ignore next -- @preserve */ async ({ matterDeviceId }) => {
        try {
          await Matter.unpairDevice({ deviceId: matterDeviceId });
          return { data: undefined };
        } catch (e) {
          Sentry.captureException(e, {
            tags: {
              category: 'feature',
              feature: 'ayla-switches',
              helper: 'localAylaMatterDevicesApi',
              method: 'deleteLocalAylaMatterDevice',
            },
          });
          const error = e as { message: ErrorStatus };
          console.error(error);
          const message = error?.message ?? 'Error removing local Matter device';
          return { error: { status: 500, message } as RTKQError };
        }
      },
      invalidatesTags: ['LocalMatterDevices'],
    }),
    generateLocalAylaMatterPairingCode: builder.mutation<string, { matterDeviceId: string }>({
      queryFn: /* istanbul ignore next -- @preserve */ async ({ matterDeviceId }) => {
        try {
          const { manualCode } = await Matter.openCommissioningWindow({
            deviceId: matterDeviceId,
            durationSeconds: MATTER_OPEN_COMMISSIONING_WINDOW_DURATION_SECONDS,
          });
          return { data: manualCode };
        } catch (e) {
          Sentry.captureException(e, {
            tags: {
              category: 'feature',
              feature: 'ayla-switches',
              helper: 'localAylaMatterDevicesApi',
              method: 'generateLocalAylaMatterPairingCode',
            },
          });
          const error = e as { message: ErrorStatus };
          console.error(error);
          const message = error?.message ?? 'Error generating pairing code for local Matter Device';
          return { error: { status: 500, message } as RTKQError };
        }
      },
    }),
    turnLocalAylaMatterDeviceOnOff: builder.mutation<void, { matterDeviceId: string; state: 'open' | 'closed' }>({
      queryFn: async ({ matterDeviceId, state }) => {
        try {
          await Matter.commandOnOff({
            deviceId: matterDeviceId,
            value: state === 'closed',
            endpointId: MATTER_ON_OFF_ENDPOINT_ID,
          });
          return { data: undefined };
        } catch (e) {
          Sentry.captureException(e, {
            tags: {
              category: 'feature',
              feature: 'ayla-switches',
              helper: 'localAylaMatterDevicesApi',
              method: 'turnLocalAylaMatterDeviceOnOff',
            },
          });
          const error = e as { message: ErrorStatus };
          console.error(error);
          const message = error?.message ?? 'Error controlling local Matter device';
          return { error: { status: 500, message } as RTKQError };
        }
      },
      invalidatesTags: ['LocalMatterDevices'],
    }),
  }),
});

export const {
  useGetLocalAylaMatterDevicesQuery,
  useCommissionLocalAylaMatterDeviceAndroidMutation,
  useCommissionLocalAylaMatterDeviceIOSMutation,
  useDeleteLocalAylaMatterDeviceMutation,
  useGenerateLocalAylaMatterPairingCodeMutation,
  useTurnLocalAylaMatterDeviceOnOffMutation,
} = localAylaMatterDevicesApi;

/**
 * Fetches a local matter device and its details via our Matter native wrapper plugin.
 *
 * @param serialNumber - The Ayla switch's serial number, e.g. AC000W029168787
 * @param skip - Used to skip the query as needed, defaults to false, automatically skips on native
 * @returns Matter device details we were able to grab locally from the device itself
 */
export function useGetLocalAylaMatterDevice(serialNumber?: string, skip = false) {
  const { data: localMatterDevice, ...rest } = useGetLocalAylaMatterDevicesQuery(undefined, {
    skip: !IS_NATIVE || skip || !serialNumber,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
    selectFromResult: ({ data, ...rest }) =>
      /* istanbul ignore next -- @preserve */
      ({
        ...rest,
        data: data?.find((s: LocalMatterDevice) => s?.serialNumber === serialNumber),
      }),
  });

  return {
    data: localMatterDevice,
    ...rest,
  };
}
